/**
 * Who is here, and what they are looking at.
 *
 * WHY THIS EXISTS
 *   Six engineers share one archive of drawings. Knowing that Sudipta has the
 *   gun-housing assembly open right now is what stops two people editing the
 *   same part in parallel — and it is the difference between a file server and
 *   something that feels like working together.
 *
 * DATA DEFINITIONS
 *
 *   A Presence is { userId, state, since, lastSeenAt, projectId?, path? }.
 *   Interpretation: what we know about one person's connection.
 *     - state "online"  they hold at least one open stream right now
 *     - state "away"    they were here recently and may come back
 *     - state "offline" no open stream; `lastSeenAt` is when they left
 *   `since` is when the current state began, so the interface can say
 *   "online for 20 minutes" or "last seen 3 hours ago" without a second field.
 *   `projectId`/`path` are what they last looked at, so we can show it beside
 *   their avatar. They are advisory: a person's presence never gates an action.
 *
 *   A PresenceMap is a Map from UserId to Presence, covering EVERY member of
 *   the roster — including people who have never signed in, who are reported
 *   as offline with no lastSeenAt. A view that must render six avatars should
 *   not have to reason about absent keys.
 */
import { ROSTER } from './user.js';
import {} from './change.js';
import {} from './path.js';
/**
 * A person is "away" rather than "online" once their stream has been silent
 * this long. Two missed heartbeats: long enough that a garbage-collection
 * pause or a tunnel does not flip the dot, short enough to be believable.
 */
export const AWAY_AFTER_MS = 45_000;
/** How often a connected client tells the server it is still there. */
export const HEARTBEAT_MS = 15_000;
/** Everyone offline, which is what a client knows before its stream opens. */
export function emptyPresence(now) {
    return new Map(ROSTER.map(u => [u.id, { userId: u.id, state: 'offline', since: now }]));
}
/**
 * Fold the server's presence rows over the full roster.
 *
 * The server only sends rows for people it knows something about; everyone
 * else is offline. Doing that fill here rather than in a component means every
 * view agrees about what "nobody has ever signed in" looks like.
 * mergePresence : [Presence] String -> PresenceMap
 */
export function mergePresence(rows, now) {
    const out = new Map(emptyPresence(now));
    for (const row of rows)
        if (out.has(row.userId))
            out.set(row.userId, row);
    return out;
}
/**
 * Re-grade "online" to "away" for anyone whose last sighting is stale.
 *
 * Interpretation: the server grades presence when it sends it, but a client
 * holding an old snapshot would otherwise show a stale green dot forever. This
 * lets the client age its own copy without another round trip.
 * agePresence : PresenceMap Number -> PresenceMap
 */
export function agePresence(presence, nowMs) {
    let changed = false;
    const out = new Map(presence);
    for (const [id, p] of presence) {
        if (p.state !== 'online' || !p.lastSeenAt)
            continue;
        if (nowMs - Date.parse(p.lastSeenAt) <= AWAY_AFTER_MS)
            continue;
        out.set(id, { ...p, state: 'away', since: p.lastSeenAt });
        changed = true;
    }
    return changed ? out : presence;
}
/**
 * The people to show as present, most-recently-active first, excluding the
 * viewer themselves — nobody needs to be told that they are online.
 * othersPresent : PresenceMap UserId -> [Presence]
 */
export function othersPresent(presence, me) {
    return [...presence.values()]
        .filter(p => p.userId !== me && p.state !== 'offline')
        .sort((a, b) => (b.lastSeenAt ?? b.since).localeCompare(a.lastSeenAt ?? a.since));
}
/**
 * Everyone who has ever been seen, ordered for a "who was here" list: online
 * first, then away, then offline by how recently they were last seen.
 * presenceRoster : PresenceMap -> [Presence]
 */
export function presenceRoster(presence) {
    const rank = { online: 0, away: 1, offline: 2 };
    return [...presence.values()].sort((a, b) => {
        if (rank[a.state] !== rank[b.state])
            return rank[a.state] - rank[b.state];
        const at = a.lastSeenAt ?? a.since;
        const bt = b.lastSeenAt ?? b.since;
        return bt.localeCompare(at);
    });
}
/** Who, other than `me`, currently has this exact file open. viewersOf : ... -> [Presence] */
export function viewersOf(presence, projectId, path, me) {
    return [...presence.values()].filter(p => p.userId !== me && p.state === 'online' && p.projectId === projectId && p.path === path);
}
//# sourceMappingURL=presence.js.map